import React, { useMemo, useCallback } from "react";
import { useDispatch } from "react-redux";
import { useHistory, useParams } from "react-router-dom";

import { Button } from "components";
import { LineDivider } from "components/layout";
import {
	useApproveMutation,
	useDeclineMutation,
	useOpenedPendsMutation,
	useUpdateApprovalMutation,
} from "queries";
import { uiActions } from "store";
import { UWDecisions } from "types";
import {
	uwApproveDecisions,
	uwDeclineDecisions,
} from "types/enums/uwDecisions";

import getApproveNoteText from "./getApproveNoteText";

import styles from "./underwritingForm.module.scss";

const UnderwritingDecisionActions = ({
	isFormDisabled,
	decisionData,
	isConcurrenceTab,
}) => {
	const dispatch = useDispatch();
	const history = useHistory();
	const { tab, id } = useParams();

	const { mutateAsync: getOpenedPends } = useOpenedPendsMutation();
	const { mutate: onApprove } = useApproveMutation();
	const { mutate: onUpdateApproval } = useUpdateApprovalMutation();
	const { mutate: onDecline } = useDeclineMutation();

	const isDecisionCanEdit = useMemo(
		() =>
			decisionData?.underwritingDecision ===
				UWDecisions.ApprovedNeedsSponsorBankConcurrence.name ||
			decisionData?.underwritingDecision ===
				UWDecisions.ApprovedWithConditions.name,
		[decisionData?.underwritingDecision],
	);

	const approveNoteText = useMemo(
		() => getApproveNoteText(decisionData),
		[decisionData],
	);

	const showAlertPendModal = pends => {
		dispatch(
			uiActions.showModal("ALERT_PEND", {
				pends,
			}),
		);
	};

	const showDeclineModal = () => {
		dispatch(
			uiActions.showModal("UW_DECISION", {
				decisions: uwDeclineDecisions,
				onSubmit: values =>
					onDecline({ data: values, applicationId: id }),
				title: "Underwriting Decline",
				subTitle: "Are you sure you want to decline the application",
				buttonText: "Decline",
				defaultValues: {
					decision: UWDecisions.DeclinedDoesNotMeetJurisdictionReqs,
					title: `Underwriting ${UWDecisions.DeclinedDoesNotMeetJurisdictionReqs.displayName}`,
					note: "",
				},
			}),
		);
	};

	const showApproveModal = () => {
		dispatch(
			uiActions.showModal("UW_DECISION", {
				decisions: uwApproveDecisions,
				onSubmit: values =>
					onApprove({ data: values, applicationId: id }),
				defaultValues: {
					decision: UWDecisions.Approved,
					title: `Underwriting ${UWDecisions.Approved.displayName}`,
					note: approveNoteText,
				},
			}),
		);
	};

	const showUpdateApprovalModal = () => {
		dispatch(
			uiActions.showModal("UW_DECISION", {
				decisions: uwApproveDecisions,
				title: "Update Approval for the Application?",
				buttonText: "Update Approval",
				onSubmit: values =>
					onUpdateApproval({
						data: values,
						applicationId: id,
					}),
				defaultValues: {
					decision: UWDecisions.Approved,
					title: `Underwriting ${UWDecisions.Approved.displayName}`,
					note: approveNoteText,
				},
			}),
		);
	};

	const handleDecline = async () => {
		const openPends = await getOpenedPends(id);

		if (openPends?.items.length > 0) {
			showAlertPendModal(openPends?.items);
		} else {
			showDeclineModal();
		}
	};

	const handleUpdateDecline = () => handleDecline();

	const handleApprove = async () => {
		const openPends = await getOpenedPends(id);

		if (openPends?.items.length > 0) {
			showAlertPendModal(openPends?.items);
		} else {
			showApproveModal();
		}
	};

	const handleUpdateApproval = () => showUpdateApprovalModal();

	const redirectToAddPendPage = useCallback(async () => {
		history.push(`/underwriting/${tab}/${id}/pend/add`);
	}, [history, id, tab]);

	return (
		<div className={styles.wrappUwBtn}>
			<LineDivider />
			<div className={styles.wrappFooterBtn}>
				<div className={styles.footerBtn}>
					{isConcurrenceTab ? (
						<Button
							className={styles.declineBtn}
							onClick={handleUpdateDecline}
							value="Decline"
							variant="danger"
							disabled={!isDecisionCanEdit}
						/>
					) : (
						<Button
							className={styles.declineBtn}
							onClick={handleDecline}
							value="Decline"
							variant="danger"
						/>
					)}
				</div>
				<div className={styles.footerBtn}>
					<Button
						className={styles.pendedBtn}
						onClick={redirectToAddPendPage}
						value="Pended"
						variant="warning"
						disabled={isConcurrenceTab}
					/>
				</div>
				<div className={styles.footerBtn}>
					{isConcurrenceTab ? (
						<Button
							className={styles.approveBtn}
							onClick={handleUpdateApproval}
							value="Update Approval"
							variant="success"
							disabled={!isDecisionCanEdit}
						/>
					) : (
						<Button
							className={styles.approveBtn}
							disabled={isFormDisabled}
							onClick={handleApprove}
							value="Approve"
							variant="success"
						/>
					)}
				</div>
			</div>
		</div>
	);
};

export default UnderwritingDecisionActions;
